import { buildParams } from '@/helpers/url';

const initialState = {
  reviewMethods: [
    {
      id: 0,
      name: 'All review method',
      key: 'all',
    },
    {
      id: 1,
      name: 'Manager review',
      key: 'manager-review',
    },
    {
      id: 2,
      name: '360-degree review',
      key: 'threesixty-review',
    },
    {
      id: 3,
      name: 'Team review',
      key: 'peer-to-peer',
    },
    {
      id: 4,
      name: 'Self review',
      key: 'self-review',
    },
  ],
  reviewMethodsMapping: {
    'manager-review': 'Manager review',
    'threesixty-review': '360-degree review',
    'peer-to-peer': 'Team review',
    'self-review': 'Self review',
  },
  reviewStatusMapping: {
    0: 'Not started',
    1: 'Save as draft',
    2: 'Submitted',
    3: 'Pending',
    4: 'Approved',
    5: 'Rejected',
  },
  assessmentPurposeOptions: [
    { id: 1, name: 'Assess employee competency for current position' },
    { id: 2, name: 'Assess employee of successor for succession plan' },
  ],
  publishMethod: [
    { name: 'Complete review', value: 'complete-cycle' },
    { name: 'Review period end', value: 'cycle-end' },
    { name: 'Both', value: 'complete-review-and-cycle-end' },
  ],
  cyclePurposes: [
    { name: 'Performance', value: 'performance', icon: 'performance' },
    { name: 'Competency', value: 'competency', icon: 'competencies' },
    { name: 'Evaluation', value: 'probation', icon: 'evaluation' },
  ],
  cyclePurposesMapping: {
    performance: 'Performance',
    competency: 'Competency',
    probation: 'Evaluation',
  },
};

const getters = {
  assessmentPurposeOptions(state) {
    return state.assessmentPurposeOptions;
  },
  reviewMethods(state) {
    return state.reviewMethods;
  },
  reviewStatusMapping(state) {
    return state.reviewStatusMapping;
  },
};

function buildParamsModified(params) {
  const searchParams = {};

  if (params.current_page) searchParams.page = params.current_page;
  if (params.per_page) searchParams.limit_per_page = params.per_page;
  if (params.sort) searchParams.sort_field_data = params.sort;
  if (params.search) searchParams.search_data = params.search;
  if (params.task_status) searchParams.task_status = params.task_status;
  if (params.company_id) searchParams.company_id = params.company_id;

  return new URLSearchParams(searchParams);
}

const actions = {
  async fetchCycleMasters(context, params) {
    const queries = buildParams(params);
    const url = `${this._vm.$endPoint}/cycle-masters?`;

    try {
      const { data, response } = await this._vm.$axios.get(url + queries);

      if (data?.contents) {
        return data.contents?.cycle_masters;
      }
      throw response?.data?.message || data?.message;
    } catch (err) {
      throw Error(err);
    }
  },
  async toggleAutoCycleMaster(context, params) {
    const url = `${this._vm.$endPoint}/cycle-masters/toggle-automatic-cycle`;

    try {
      const { data, response } = await this._vm.$axios.post(url, params);

      if (data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message;
    } catch (err) {
      throw Error(err);
    }
  },
  async deleteCycleMaster(context, uuid) {
    const url = `${this._vm.$endPoint}/cycle-masters/${uuid}`;

    try {
      const { data, response } = await this._vm.$axios.delete(url);

      if (data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message;
    } catch (err) {
      throw Error(err);
    }
  },
  async validateCycleName(context, data) {
    try {
      const url = `${this._vm.$endPoint}/cycle-masters/validate-cycle-name`;

      const res = await this._vm.$axios.post(url, data);

      if (res.data?.status_code === 200 || res.data?.status === 200) return { valid: res?.data?.contents, message: res?.data?.message };
      throw res?.response;
    } catch (err) {
      /* eslint-disable no-throw-literal */
      if (err.response) {
        throw err.response.data;
      } else {
        throw { message: 'Failed to validate name' };
      }
    }
  },
  async getMembers(context, params) {
    const queries = buildParams(params);
    const url = `${this._vm.$endPoint}/users/cycle-members?`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(url + queries);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async getKeyPositions() {
    const url = `${this._vm.$endPoint}/cycle-masters/key-position`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(url);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async initCreateCycle() {
    const url = `${this._vm.$endPoint}/cycle-masters/init-add-cycle-master`;

    try {
      const { data, response } = await this._vm.$axios.get(url);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || 'Failed to get initial data';
    } catch (err) {
      throw Error(err);
    }
  },
  async createReviewCycle(context, payload) {
    try {
      const url = `${this._vm.$endPoint}/cycle-masters/new`;

      const { data, response } = await this._vm.$axios.post(url, payload);

      if (data?.status_code === 200 || data?.status === 200) return 'New cycle is created';
      throw response?.data || data;
    } catch (err) {
      throw err || { message: 'Failed to create cycle' };
    }
  },
  async downloadReviewResult(context, payload) {
    try {
      const url = `${this._vm.$endPoint}/review-reports/${payload.url}`;
      const { data, statusMessage, response } = await this._vm.$axios.post(url, payload.payload);
      if (data?.status_code === 200) return data;
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async getMyReviews(context, params) {
    const queries = buildParamsModified(params);
    const reviewPath = `${this._vm.$endPoint}/performance-reviews?`;

    try {
      const { data } = await this._vm.$axios.get(reviewPath + queries);
      const memberPerformanceReviews = data.contents ? data.contents.member_performance_reviews : null;

      if (memberPerformanceReviews) return memberPerformanceReviews;
      throw Error('Invalid response');
    } catch (err) {
      throw Error(err);
    }
  },
  async getTotalPendingTask(context, params) {
    // use company id only when in pending action active index & detail
    const reviewPath = `${this._vm.$newEndPoint}/tasks/total-pending-action?company_id=${params || ''}`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(reviewPath);

      if (data?.contents) {
        const totalPendingTask = (data?.contents?.total_pending_action || 0).toString();
        if (totalPendingTask) {
          // pages other than pending active that use filter company
          if (!params) {
            this._vm.$cookies.set('total-pending-task', totalPendingTask);
            this._vm.$cookies.set('total-pending-task-filtered-company', totalPendingTask);
          } else this._vm.$cookies.set('total-pending-task-filtered-company', totalPendingTask);
          context.commit('SET_SIDEBAR_NOTIF', { pending_action: totalPendingTask }, { root: true });
        }
        return true;
      }
      throw response?.data?.message ?? data?.message ?? statusMessage ?? 'Failed to get total pending task';
    } catch (err) {
      throw Error(err);
    }
  },
  async getCurrentTasks(context, params) {
    const queries = buildParamsModified(params);
    const reviewPath = `${this._vm.$newEndPoint}/tasks/current?`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(reviewPath + queries);

      if (data?.contents) {
        context.dispatch('getTotalPendingTask', params.company_id);
        return data.contents?.tasks;
      }
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async getHistoryTasks(context, params) {
    const queries = buildParamsModified(params);
    const reviewPath = `${this._vm.$newEndPoint}/tasks/history?`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(reviewPath + queries);

      if (data?.contents?.tasks) {
        context.dispatch('getTotalPendingTask', params.company_id);
        return data.contents?.tasks;
      }
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async getGoalList(context, uuid) {
    const url = `${this._vm.$endPoint}/cycle-members/list-pick-goals/${uuid}`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(url);

      if (data?.status_code === 200 && data?.contents) return data.contents;
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async getTaskDetail(context, params) {
    if (!params.uuid) throw Error('Invalid UUID');

    const queries = buildParams(params.pagination);
    const mode = params.current ? 'current' : 'history';
    const reviewPath = `${this._vm.$newEndPoint}/tasks/${mode}/${params.uuid}?`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(reviewPath + queries);
      if (data?.status_code === 200 && data?.contents) {
        if (data.contents.company_id) context.dispatch('getTotalPendingTask', data.contents.company_id);

        return data.contents;
      }
      throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async submitResetReview(context, params) {
    try {
      const reviewPath = `${this._vm.$endPoint}/results/delete-multiple-results`;

      const { data } = await this._vm.$axios.post(reviewPath, params);

      if (!data.contents) throw Error('Invalid response');
      return data.contents.review_statuses;
    } catch (err) {
      throw Error(err);
    }
  },
  async submitRejectReview(context, params) {
    try {
      const url = `${this._vm.$endPoint}/tasks/reject`;

      const { data, response, statusMessage } = await this._vm.$axios.post(url, params);

      if (data?.status_code !== 200) throw response?.data?.message || data?.message || statusMessage;
    } catch (err) {
      throw Error(err);
    }
  },
  async submitCoworker(context, payload) {
    const coworkersPath = `${this._vm.$endPoint}/co-workers`;

    try {
      const { data } = await this._vm.$axios.post(coworkersPath, payload);

      if (data.contents) return data.contents;
      throw Error('Invalid response');
    } catch (err) {
      throw Error(err);
    }
  },
  async getResult(context, payload) {
    const reviewPath = `${this._vm.$endPoint}/performance-reviews`;

    try {
      const { data, response } = await this._vm.$axios.post(reviewPath, payload);

      if ((data?.status === 200 || data?.status_code === 200) && data.contents) return data;

      throw response?.data?.message || data?.message || 'Failed to fetch review result';
    } catch (err) {
      throw Error(err);
    }
  },
  async getIsFirstrun() {
    const url = `${this._vm.$endPoint}/dashboard/reviews/check-first-run`;

    try {
      const { data, status, statusMessage } = await this._vm.$axios.get(url);
      if (status === 200) {
        return data.is_first_run;
      }

      throw Error(statusMessage || data.message);
    } catch (err) {
      throw Error(err);
    }
  },
  async getCycleByPeriod(context, payload) {
    const url = `${this._vm.$endPoint}/cycle-masters/by-period`;

    try {
      const { data, statusMessage } = await this._vm.$axios.post(url, payload);

      if ((data.status === 200 || data.status_code === 200) && data.contents) {
        return data.contents.cycle_masters;
      }

      throw Error(statusMessage || data.message);
    } catch (err) {
      throw Error(err);
    }
  },
  async getCycleTimeframes(context, uuid) {
    const url = `${this._vm.$endPoint}/cycles/${uuid}`;

    try {
      const { data, response } = await this._vm.$axios.get(url);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || 'Failed to fetch cycle detail';
    } catch (err) {
      throw Error(err);
    }
  },
  async deleteCycleTimeframe(context, uuid) {
    const url = `${this._vm.$endPoint}/cycles/${uuid}`;

    try {
      const { data, response } = await this._vm.$axios.delete(url);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || 'Failed to delete cycle';
    } catch (err) {
      throw Error(err);
    }
  },
  async publishScore(context, uuid) {
    const url = `${this._vm.$endPoint}/cycles/publish-score/${uuid}`;

    try {
      const { data, response } = await this._vm.$axios.put(url);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || 'Failed to publish score';
    } catch (err) {
      throw Error(err);
    }
  },
  async extendPeriod(context, payload) {
    const url = `${this._vm.$endPoint}/cycles/extend-period`;

    try {
      const { data, response } = await this._vm.$axios.post(url, payload);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || 'Failed to extend period';
    } catch (err) {
      throw Error(err);
    }
  },
  async getCycleDetail(context, uuid) {
    const url = `${this._vm.$endPoint}/cycles/show/${uuid}`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(url);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents || {};
      }
      throw response?.data?.message || data?.message || statusMessage || 'Error to get cycle detail';
    } catch (err) {
      throw Error(err);
    }
  },
  async getCycleMembers(context, params) {
    const queries = buildParams(params.payload);
    const url = `${this._vm.$endPoint}/cycles/list-members/${params.uuid}?`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.get(url + queries);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents || {};
      }
      throw response?.data?.message || data?.message || statusMessage || 'Failed to get cycle members';
    } catch (err) {
      throw Error(err);
    }
  },
  async postAddMember(context, payload) {
    const url = `${this._vm.$endPoint}/cycle-members`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.post(url, payload);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents || {};
      }
      throw response?.data?.message || data?.message || statusMessage || 'Failed to add cycle member';
    } catch (err) {
      throw Error(err);
    }
  },
  async postAddMultipleReviewer(context, payload) {
    const url = `${this._vm.$endPoint}/reviewers/new-multiple-member`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.post(url, payload);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents || {};
      }
      throw response?.data?.message || data?.message || statusMessage || 'Failed to add reviewer';
    } catch (err) {
      throw Error(err);
    }
  },
  async postAddReviewer(context, payload) {
    const url = `${this._vm.$newEndPoint}/reviewers/new`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.post(url, payload);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents || {};
      }
      throw response?.data?.message || data?.message || statusMessage || 'Error on save reviewer';
    } catch (err) {
      throw Error(err);
    }
  },
  async deleteMember(context, payload) {
    const url = `${this._vm.$endPoint}/cycle-members/delete-multiple-member`;

    try {
      const { data, statusMessage, response } = await this._vm.$axios.post(url, payload);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents || {};
      }
      throw response?.data?.message || data?.message || statusMessage || 'Failed to delete member';
    } catch (err) {
      throw Error(err);
    }
  },
  async getSubmitedReview(context, payload) {
    const reviewPath = `${this._vm.$endPoint}/performance-reviews/reviewer/${payload}`;

    try {
      const { data, statusMessage } = await this._vm.$axios.get(reviewPath);

      if ((data.status === 200 || data.status_code === 200) && JSON.stringify(data) !== '{}') {
        return data;
      }

      throw Error(statusMessage || data.message);
    } catch (err) {
      throw Error(err);
    }
  },
  async getAttendanceData(context, payload) {
    const url = `${this._vm.$endPoint}/results/attendance-data?reviewer_uuid=${payload}`;

    try {
      const { data, response } = await this._vm.$axios.get(url);

      if (data?.status_code === 200 && data?.contents) return data.contents.advance_score;

      throw Error(response?.data?.message || data.message || 'Failed to get attendance detail');
    } catch (err) {
      throw Error(err);
    }
  },
  async getReprimandData(context, payload) {
    const url = `${this._vm.$endPoint}/results/reprimand-data?reviewer_uuid=${payload}`;

    try {
      const { data, response } = await this._vm.$axios.get(url);

      if (data?.status_code === 200 && data?.contents) return data.contents;

      throw Error(response?.data?.message || data.message || 'Failed to get reprimand detail');
    } catch (err) {
      throw Error(err);
    }
  },
  async getReviewReportSummary(context, params) {
    const queries = buildParams(params);
    const url = `${this._vm.$endPoint}/review-reports/filter?`;

    try {
      const { data, response } = await this._vm.$axios.get(url + queries);
      if (data?.status_code === 200 && data?.contents) {
        return data.contents;
      }
      throw response?.data?.message || data?.message || 'Failed to generate report';
    } catch (err) {
      throw Error(err);
    }
  },
  async downloadReviewReportSummary(context, url) {
    try {
      const { data, response } = await this._vm.$axios.get(url);
      if (data?.status_code === 200 && data?.message === 'success') {
        return 'Download is starting, we will send the file to your email when the process is done';
      }
      throw response?.data?.message || data?.message || 'Failed to download report';
    } catch (err) {
      throw Error(err);
    }
  },
  async getReviewReportDetail(context, params) {
    const queries = buildParams(params);
    const url = `${this._vm.$endPoint}/review-reports/detail/filter?`;

    try {
      const { data, response } = await this._vm.$axios.get(url + queries);
      if (data?.status_code === 200 && data?.contents?.cycle_members_users) {
        return data.contents.cycle_members_users;
      }
      throw response?.data?.message || data?.message || 'Failed to generate report';
    } catch (err) {
      throw Error(err);
    }
  },
  async downloadReviewReportDetail(context, payload) {
    try {
      const { data, response } = await this._vm.$axios.post(payload.url, payload.data);
      if (data?.status_code === 200 && data?.message === 'success') {
        return 'Download is starting, we will send the file to your email when the process is done';
      }
      throw response?.data?.message || data?.message || 'Failed to download report';
    } catch (err) {
      throw Error(err);
    }
  },
};

export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  actions,
  getters,
};
